import React from 'react';
import { Box, CheckboxV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { InternalMessage } from '../../MessagesV2.types';
import { isResendable } from './resendSelection.utils';

type ResendCheckboxCellProps = Readonly<{
  message: InternalMessage;
  isSelected: boolean;
  onToggle: (id: string) => void;
}>;

/**
 * The per-row selection checkbox.
 *
 * SMS rows render the checkbox **present but disabled** rather than omitting it. A missing control
 * on some rows reads as a rendering bug; a greyed one with a tooltip reads as "not yet". The
 * backend SMS resend exists and is tested — turning it on here is a UI-only change.
 *
 * Clicks are stopped from bubbling: the row itself is a click target that expands it, and ticking
 * a checkbox should not also open the row.
 */
export const ResendCheckboxCell: React.FC<ResendCheckboxCellProps> = ({ message, isSelected, onToggle }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const resendable = isResendable(message);

  const checkbox = (
    <CheckboxV2
      isChecked={resendable && isSelected}
      isDisabled={!resendable}
      onChange={() => onToggle(message.id)}
      aria-label={resendable ? tq`Select message for resend` : tq`SMS resend is not available yet`}
    />
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      {resendable ? checkbox : <TooltipV2 content={tq`SMS resend isn't available yet`}>{checkbox}</TooltipV2>}
    </Box>
  );
};
